import i18n from "../../i18n";
import sessionDB from "../../utils/sessionDB";

const ACTION_KEYWORDS = {
  add: ["add", "create", "save", "allocate"],
  update: ["update", "edit"],
  search: ["search", "list", "view", "get"],
  hierarchy: ["hierarchy"],
  upload: ["upload"],
  download: ["download"]
};

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function isDisplayEnabled(feature) {
  return feature?.display === true;
}

function getOnboardingModule() {
  const enabledServices = sessionDB.getEnabledServices();
  const hrmsModules = enabledServices?.["HRMS Team"] || enabledServices?.HRMS || {};
  return hrmsModules?.Onboarding || {};
}

export function getOnboardingFeatures(submoduleNames = []) {
  const onboardingModule = getOnboardingModule();

  for (const submoduleName of submoduleNames) {
    const features = onboardingModule?.[submoduleName];
    if (Array.isArray(features) && features.length > 0) {
      return features.filter((feature) => isDisplayEnabled(feature) && feature?.code && feature?.api);
    }
  }

  return [];
}

export function resolveOnboardingActionConfig({
  submoduleNames = [],
  action,
  fallback
}) {
  const features = getOnboardingFeatures(submoduleNames);
  const actionKeywords = ACTION_KEYWORDS[action] || [action];
  const fallbackEndpoint = normalizeText(fallback?.endpoint);
  const fallbackTitle = normalizeText(fallback?.title);

  const match = features.find((feature) => {
    const featureName = normalizeText(feature?.feature);
    const featureApi = normalizeText(feature?.api);

    return actionKeywords.some((keyword) => {
      const normalizedKeyword = normalizeText(keyword);
      return (
        featureName.includes(normalizedKeyword) ||
        featureApi.includes(normalizedKeyword) ||
        (fallbackEndpoint && featureApi === fallbackEndpoint) ||
        (fallbackTitle && featureName === fallbackTitle)
      );
    });
  });

  if (match) {
    return {
      ...fallback,
      feature: match.feature,
      serviceCode: match.code,
      endpoint: match.api,
      isConfigured: true,
      isAvailable: true
    };
  }

  return {
    ...fallback,
    isConfigured: true,
    isAvailable: true
  };
}

export function buildOnboardingAccessMessage(submoduleLabel, actionLabel) {
  return `${actionLabel} is not configured or not permitted for ${submoduleLabel} in this tenant.`;
}

export function isOnboardingFeatureUnavailableError(message) {
  const msgStr = String(message || "");
  return (
    msgStr.includes("Service_Is_Not_Configured") ||
    msgStr.includes("not configured") ||
    msgStr.includes("not permitted") ||
    msgStr.includes("URL Resource not found") ||
    msgStr.includes("No static resource")
  );
}

const GENERIC_ERROR_PATTERNS = [
  /^validation failed/i,
  /^something went wrong/i,
  /^please fix the highlighted fields/i,
  /^please fix the validation errors/i,
  /^an error occurred/i,
  /^bad request$/i,
  /^request failed$/i,
  /^search failed\.?$/i,
  /^download failed\.?$/i,
  /^error$/i,
  /^failed$/i,
  /^server error \(\d+\)$/i
];

export function isGenericOnboardingErrorMessage(message) {
  const msg = String(message || "").trim();
  if (!msg) return true;
  return GENERIC_ERROR_PATTERNS.some((pattern) => pattern.test(msg));
}

const RATE_LIMIT_TEXT_PATTERNS = [
  /too many requests?/i,
  /rate limit/i,
  /request limit/i,
  /throttl/i,
  /100\s*\+?\s*requests?/i,
  /exceeded.*limit/i
];

export function isLookupRateLimitError(message, status, errorBody) {
  const msg = String(message || "").trim();
  const lookupFailedMatch = msg.match(/^lookup request failed \((\d+)\)$/i);
  const statusNum = Number.isFinite(Number(status))
    ? Number(status)
    : Number(lookupFailedMatch?.[1]);

  if (statusNum === 429) return true;

  const bodyText = [
    errorBody?.message,
    errorBody?.error,
    typeof errorBody === "string" ? errorBody : ""
  ]
    .filter(Boolean)
    .join(" ");

  const combined = `${msg} ${bodyText}`.trim();
  if (RATE_LIMIT_TEXT_PATTERNS.some((pattern) => pattern.test(combined))) {
    return true;
  }

  return (
    statusNum >= 500 &&
    /^lookup request failed/i.test(msg)
  );
}

function humanizeFieldName(field, fieldLabels = {}) {
  if (fieldLabels[field]) return fieldLabels[field];
  return String(field)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function formatFieldErrorMessage(field, message, fieldLabels = {}) {
  const label = humanizeFieldName(field, fieldLabels);
  const msg = String(message || "").trim();
  if (!msg) return `${label} is required`;
  if (/^(required|must not be (blank|null|empty)|cannot be null|cannot be empty)$/i.test(msg)) {
    return `${label} is required`;
  }
  if (/^invalid/i.test(msg) && !msg.toLowerCase().includes(label.toLowerCase())) {
    return `Invalid ${label.toLowerCase()}`;
  }
  if (msg.toLowerCase().includes(label.toLowerCase()) || msg.toLowerCase().includes(String(field).toLowerCase())) {
    return msg.charAt(0).toUpperCase() + msg.slice(1);
  }
  return `${label}: ${msg}`;
}

function parseCompoundValidationMessage(message) {
  const match = String(message || "").trim().match(/^validation failed\s*[-:]\s*(.+)$/i);
  return match ? match[1].trim() : null;
}

function normalizeFieldKey(field) {
  return String(field || "")
    .trim()
    .replace(/^\[?'?(.+?)'?]?$/, "$1")
    .split(".")
    .pop();
}

function addFieldError(target, field, message, fieldLabels) {
  const key = normalizeFieldKey(field);
  if (!key) return;
  target[key] = formatFieldErrorMessage(key, message, fieldLabels);
}

export function collectOnboardingFieldErrors(body, fieldLabels = {}) {
  const errors = {};
  if (!body || typeof body !== "object") return errors;

  const objectCollections = [
    body.fieldErrors,
    body.validationErrors,
    body.errors,
    body.data?.fieldErrors,
    body.data?.validationErrors,
    body.data?.errors
  ];

  for (const collection of objectCollections) {
    if (!collection || Array.isArray(collection) || typeof collection !== "object") continue;
    for (const [field, message] of Object.entries(collection)) {
      if (String(field).toLowerCase() === "message") continue;
      if (typeof message === "string" && message.trim()) {
        addFieldError(errors, field, message, fieldLabels);
      } else if (Array.isArray(message) && message.length > 0) {
        addFieldError(
          errors,
          field,
          message.map((item) => String(item || "").trim()).filter(Boolean).join(", "),
          fieldLabels
        );
      }
    }
  }

  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    const skipKeys = new Set(["message", "error", "status", "code", "success", "timestamp"]);
    for (const [field, message] of Object.entries(body.data)) {
      if (skipKeys.has(field)) continue;
      if (typeof message === "string" && message.trim()) {
        addFieldError(errors, field, message, fieldLabels);
      }
    }
  }

  const arrayCollections = [
    body.details,
    body.errors,
    body.validationErrors,
    body.fieldErrors,
    body.data?.details,
    body.data?.errors
  ];

  for (const collection of arrayCollections) {
    if (!Array.isArray(collection)) continue;
    for (const item of collection) {
      if (!item || typeof item !== "object") continue;
      const field = item.field || item.name || item.property || item.path || item.fieldName;
      const message = item.message || item.defaultMessage || item.error || item.reason;
      if (field && message) addFieldError(errors, field, message, fieldLabels);
    }
  }

  return errors;
}

export function applyOnboardingFieldErrors(body, setErrors, setTouched, fieldLabels = {}) {
  const fieldErrors = collectOnboardingFieldErrors(body, fieldLabels);
  const keys = Object.keys(fieldErrors);
  if (!keys.length) return false;

  if (setErrors) {
    setErrors((prev) => ({ ...prev, ...fieldErrors }));
  }
  if (setTouched) {
    setTouched((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = true;
      });
      return next;
    });
  }
  return true;
}

export function extractOnboardingErrorMessage(body, fallback, fieldLabels = {}) {
  if (!body || typeof body !== "object") {
    const fallbackMessage = String(fallback || "").trim();
    return isGenericOnboardingErrorMessage(fallbackMessage) ? "" : fallbackMessage;
  }

  const fieldParts = Object.values(collectOnboardingFieldErrors(body, fieldLabels)).filter(Boolean);
  if (fieldParts.length) return fieldParts.join(" | ");

  const directCandidates = [body.message, body.error]
    .filter((value) => typeof value === "string" && value.trim());

  for (const direct of directCandidates) {
    const compound = parseCompoundValidationMessage(direct);
    if (compound) return compound;
    if (!isGenericOnboardingErrorMessage(direct)) return direct.trim();
  }

  if (typeof body.data === "string" && body.data.trim()) {
    const compound = parseCompoundValidationMessage(body.data);
    if (compound) return compound;
    if (!isGenericOnboardingErrorMessage(body.data)) return body.data.trim();
  }

  const detailCollections = [body.details, body.data?.details];
  for (const collection of detailCollections) {
    if (!Array.isArray(collection)) continue;
    const parts = collection
      .map((item) => {
        if (typeof item === "string") {
          return isGenericOnboardingErrorMessage(item) ? "" : item.trim();
        }
        if (!item || typeof item !== "object") return "";
        const message = item.message || item.defaultMessage || item.error || item.reason;
        return message && !isGenericOnboardingErrorMessage(message) ? String(message).trim() : "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" | ");
  }

  const fallbackMessage = String(fallback || "").trim();
  return isGenericOnboardingErrorMessage(fallbackMessage) ? "" : fallbackMessage;
}

export function handleOnboardingApiError(errorBody, errorMessage, options = {}) {
  const { setErrors, setTouched, setBanner, fieldLabels = {} } = options;

  if (applyOnboardingFieldErrors(errorBody, setErrors, setTouched, fieldLabels)) {
    return;
  }

  const resolved = String(errorMessage || "").trim();
  const compound = parseCompoundValidationMessage(resolved);
  const messageToShow = compound || (isGenericOnboardingErrorMessage(resolved) ? "" : resolved);

  if (!messageToShow || !setBanner) return;

  if (isOnboardingFeatureUnavailableError(messageToShow)) {
    setBanner({ type: "error", msg: i18n.t("onboarding.featureUnavailable") });
    return;
  }

  setBanner({ type: "error", msg: messageToShow });
}

export async function parseOnboardingErrorResponse(response, fieldLabels = {}) {
  let errorMessage = `Server error (${response.status})`;
  let errorBody = null;

  try {
    errorBody = await response.json();
    errorMessage = extractOnboardingErrorMessage(errorBody, errorMessage, fieldLabels);
  } catch {
    try {
      const text = await response.text();
      if (text?.trim()) errorMessage = text.trim();
    } catch {}
  }

  if (isGenericOnboardingErrorMessage(errorMessage)) {
    errorMessage = extractOnboardingErrorMessage(errorBody, "", fieldLabels);
  }

  return { errorMessage, errorBody };
}
